/**
 * Device and capability sniffing.
 *
 * Guarded so this module imports cleanly outside a browser — the unit suite
 * runs it under Bun, with no DOM at all.
 */

export type DeviceTier = 'low' | 'mid' | 'high';

const media = (q: string): boolean =>
  typeof matchMedia === 'function' ? matchMedia(q).matches : false;

export const isTouch: boolean =
  media('(hover: none)') || (globalThis.navigator?.maxTouchPoints ?? 0) > 1;

export const isCoarse: boolean = media('(pointer: coarse)');

/** Phones stop short of this, in CSS pixels along their shorter side. */
const TABLET_MIN = 600;

const shortSide = (): number => {
  const scr = globalThis.screen as Screen | undefined;
  return scr ? Math.min(scr.width, scr.height) : 0;
};

/**
 * A touch screen big enough to be a tablet rather than a phone. The deck is
 * designed around one lying flat, and it is the one device the app offers to
 * take over the whole screen on.
 */
export const isTablet: boolean = isCoarse && shortSide() >= TABLET_MIN;

interface NavigatorHints {
  deviceMemory?: number;
  hardwareConcurrency?: number;
}

/** A rough performance budget: drives pixel ratio, shadow map size and antialiasing. */
export function deviceTier(): DeviceTier {
  const nav = (globalThis.navigator ?? {}) as NavigatorHints;
  const mem = nav.deviceMemory ?? 4;
  const cores = nav.hardwareConcurrency ?? 4;
  const px = globalThis.devicePixelRatio || 1;
  const scr = globalThis.screen as Screen | undefined;
  const small = scr ? Math.min(scr.width, scr.height) < 500 : false;
  if (mem <= 3 || cores <= 4 || (small && px > 2.5)) return 'low';
  if (mem >= 8 && cores >= 8) return 'high';
  return 'mid';
}
